import React, {createContext, useContext, useState, useCallback, ReactNode} from 'react'

export interface Toast {
  id: string
  title: string
  message: string
  type?: 'info' | 'success' | 'warning' | 'error'
  duration?: number
}

interface ToastContextType {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider = ({children}: {children: ReactNode}) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9)
    setToasts((prev) => {
      const newList = [...prev, {...toast, id}]
      if (newList.length > 3) return newList.slice(1) // Max 3 toasts
      return newList
    })
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return React.createElement(ToastContext.Provider, {
    value: {toasts, addToast, removeToast}
  }, children)
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
